/**
 * Wizard Analytics
 *
 * GA4 tracking for wizard/flow SPAs (e.g. Wizard-1).
 * - Tracks step views as virtual pageviews
 * - Tracks key events: flow_start, step_view, flow_complete, flow_add_to_cart, flow_resume_used, flow_error
 *
 * IMPORTANT: Assumes GA4 is already loaded globally (via gtag or dataLayer).
 * If no analytics is present, all calls safely no-op.
 *
 * Facebook Pixel tracking:
 * - Tracks step views, Lead events, and AddToCart events
 * - Dual tracking: Pixel (client-side) + Conversion API (server-side)
 */

class Wizard1Analytics {
  constructor(flowController) {
    this.flow = flowController;
    this.flowName = flowController?.getConfig?.().name || 'wizard-1';
    this.hasStarted = false;
    this.backendApiUrl = window.__wizard1BackendApiUrl || null;
    
    // Step order for step_number calculation
    this.STEP_ORDER = [
      'name', 'spayed', 'age', 'breed', 'weight', 'composition',
      'activity', 'allergies', 'snacks', 'email', 'calculation',
      'flavors', 'plan', 'bulk', 'profile'
    ];
  }

  /**
   * Safe gtag wrapper
   */
  gtag() {
    if (typeof window.gtag === 'function') {
      // eslint-disable-next-line prefer-rest-params
      window.gtag.apply(window, arguments);
    } else if (Array.isArray(window.dataLayer)) {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    } else {
      // No analytics available
    }
  }

  /**
   * Track step view (virtual pageview)
   * @param {string} step - Step identifier (e.g. 'name', 'age', 'flavors')
   */
  trackStepView(step) {
    const path = this.buildStepPath(step);
    const location = window.location.origin + path;

    // Mark flow as started on first name step
    if (!this.hasStarted && step === 'name') {
      this.hasStarted = true;
      this.trackEvent('flow_start', {
        step
      });
    }

    // GA4-style page_view for SPA
    this.gtag('event', 'page_view', {
      page_title: `${this.flowName} - ${step}`,
      page_location: location,
      page_path: path,
      flow_name: this.flowName,
      step
    });

    // Custom event for funnels
    this.trackEvent('step_view', {
      step,
      page_path: path
    });

    // Facebook Pixel tracking
    this.trackFacebookStepView(step);
  }

  /**
   * Build SPA path for a given step (mirrors router URL structure)
   */
  buildStepPath(step) {
    const config = this.flow.getConfig();
    const prefix = config?.urlPrefix || '/wizard-1';

    const quizSteps = [
      'name',
      'spayed',
      'age',
      'breed',
      'weight',
      'composition',
      'activity',
      'allergies',
      'snacks',
      'email',
      'calculation'
    ];

    const planSteps = ['flavors', 'plan', 'bulk'];

    if (quizSteps.includes(step)) {
      return `${prefix}/quiz/${step}`;
    }

    if (planSteps.includes(step)) {
      const section =
        step === 'flavors' ? 'flavors' : step === 'plan' ? 'overview' : 'bulk';
      return `${prefix}/plan/${section}`;
    }

    if (step === 'profile') {
      const metaobjectId = this.flow.state?.petMetaobjectId;
      if (!metaobjectId) {
        // Fallback if no metaobject ID (shouldn't happen for profile step)
        return `${prefix}/profile`;
      }
      return `${prefix}/profile?pet=${encodeURIComponent(metaobjectId)}`;
    }

    // Fallback
    return `${prefix}/quiz/${step}`;
  }

  /**
   * Generic event helper
   * @param {string} name - Event name
   * @param {Object} params - Event parameters
   */
  trackEvent(name, params = {}) {
    const base = {
      flow_name: this.flowName
    };
    this.gtag('event', name, Object.assign(base, params));
  }

  /**
   * Track quiz completion (called after successful add to cart)
   */
  trackCompletion(context = {}) {
    this.trackEvent('flow_complete', context);
  }

  /**
   * Track add to cart
   */
  trackAddToCart(context = {}) {
    this.trackEvent('flow_add_to_cart', context);
    
    // Note: Facebook Pixel AddToCart is tracked separately in wizard-1-steps.js
    // to avoid duplicate tracking (Shopify's native Pixel also tracks per-item)
    // Only call trackFacebookAddToCart if context has Facebook-specific data
    if (context.content_ids && context.value !== undefined) {
      this.trackFacebookAddToCart(context);
    }
  }

  /**
   * Track resume usage
   */
  trackResume(petName, source) {
    this.trackEvent('flow_resume_used', {
      pet_name: petName,
      source
    });
  }

  /**
   * Track error
   */
  trackError(message, context = {}) {
    this.trackEvent('flow_error', Object.assign({ message }, context));
  }

  /**
   * ========================================
   * FACEBOOK PIXEL TRACKING METHODS
   * ========================================
   */

  /**
   * Safe Facebook Pixel wrapper
   */
  fbq() {
    if (typeof window.fbq === 'function') {
      // eslint-disable-next-line prefer-rest-params
      window.fbq.apply(window, arguments);
    }
    // Fail silently if fbq is not available
  }

  /**
   * Generate unique event ID for deduplication
   * @param {string} eventType - Event type (e.g., 'step', 'lead', 'addtocart')
   * @returns {string} Unique event ID
   */
  generateEventId(eventType) {
    try {
      // Use crypto.randomUUID() if available (modern browsers)
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return `wizard-1-${eventType}-${crypto.randomUUID()}`;
      }
      // Fallback: timestamp-based ID
      return `wizard-1-${eventType}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    } catch (e) {
      // Ultimate fallback
      return `wizard-1-${eventType}-${Date.now()}-${Math.random()}`;
    }
  }

  /**
   * Hash email using SHA-256 (for Conversion API)
   * @param {string} email - Email address
   * @returns {Promise<string|null>} Hashed email (hex string) or null if hashing fails
   */
  async hashEmail(email) {
    if (!email || typeof email !== 'string') {
      return null;
    }

    try {
      // Normalize email: lowercase, trim whitespace
      const normalizedEmail = email.toLowerCase().trim();
      
      // Check if Web Crypto API is available
      if (typeof crypto !== 'undefined' && crypto.subtle && crypto.subtle.digest) {
        // Convert email to ArrayBuffer
        const encoder = new TextEncoder();
        const data = encoder.encode(normalizedEmail);
        
        // Hash using SHA-256
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        
        // Convert to hex string
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        
        return hashHex;
      }
      
      // Fallback: return null (backend will handle hashing)
      return null;
    } catch (e) {
      // Hashing failed, return null (backend will handle)
      return null;
    }
  }

  /**
   * Get step number from step name
   * @param {string} step - Step identifier
   * @returns {number} Step number (1-15)
   */
  getStepNumber(step) {
    const index = this.STEP_ORDER.indexOf(step);
    return index >= 0 ? index + 1 : 0;
  }

  /**
   * Track step view in Facebook Pixel
   * @param {string} step - Step identifier
   */
  trackFacebookStepView(step) {
    const stepNumber = this.getStepNumber(step);
    const eventId = this.generateEventId('step');
    
    // Custom event for step tracking
    const eventData = {
      step_name: step,
      step_number: stepNumber,
      flow_name: this.flowName,
      content_name: `Quiz Step ${stepNumber}: ${step}`
    };

    // Client-side Pixel tracking
    this.fbq('trackCustom', 'QuizStepViewed', eventData);

    // Server-side Conversion API tracking
    this.trackFacebookConversionAPI('QuizStepViewed', eventId, eventData);
  }

  /**
   * Track Lead event (email step completion)
   * @param {Object} context - Event context (pet_name, etc.)
   */
  trackFacebookLead(context = {}) {
    try {
      const eventId = this.generateEventId('lead');
      const petData = this.flow?.state?.getPetData?.() || {};
      
      const eventData = {
        content_name: 'Quiz Completed',
        flow_name: this.flowName,
        pet_name: context.pet_name || petData.name || null
      };

      // Client-side Pixel tracking (standard Lead event)
      this.fbq('track', 'Lead', eventData);

      // Server-side Conversion API tracking
      this.trackFacebookConversionAPI('Lead', eventId, eventData, {
        includeUserData: true // Include email for Lead events
      });
    } catch (e) {
      console.warn('Error tracking Facebook Lead event:', e);
    }
  }

  /**
   * Track AddToCart event
   * @param {Object} context - Event context (content_ids, value, currency, etc.)
   */
  trackFacebookAddToCart(context = {}) {
    const eventId = this.generateEventId('addtocart');
    
    // Extract cart data from context
    const contentIds = context.content_ids || [];
    const contents = context.contents || [];
    const value = context.value || 0;
    const currency = context.currency || 'EUR';

    const eventData = {
      content_ids: contentIds,
      contents: contents,
      value: value,
      currency: currency,
      flow_name: this.flowName
    };

    // Client-side Pixel tracking (standard AddToCart event)
    this.fbq('track', 'AddToCart', eventData);

    // Server-side Conversion API tracking
    this.trackFacebookConversionAPI('AddToCart', eventId, eventData, {
      includeUserData: true // Include email for AddToCart events
    });
  }

  /**
   * Send event to Facebook Conversion API via backend
   * @param {string} eventName - Event name
   * @param {string} eventId - Unique event ID for deduplication
   * @param {Object} customData - Custom event data
   * @param {Object} options - Options (includeUserData, etc.)
   */
  async trackFacebookConversionAPI(eventName, eventId, customData = {}, options = {}) {
    // Skip if backend URL is not configured
    if (!this.backendApiUrl) {
      return;
    }

    try {
      // Get user data if requested (for Lead and AddToCart events)
      let userData = null;
      if (options.includeUserData) {
        const petData = this.flow?.state?.getPetData?.() || {};
        const customerId = this.flow?.state?.customerId || null;
        
        // Hash email if available
        const emailHash = petData.ownerEmail 
          ? await this.hashEmail(petData.ownerEmail)
          : null;

        if (emailHash || customerId) {
          userData = {};
          if (emailHash) {
            userData.em = emailHash; // Hashed email
          }
          if (customerId) {
            // Extract numeric ID from GID format if needed
            const externalId = customerId.includes('/') 
              ? customerId.split('/').pop() 
              : customerId;
            userData.external_id = externalId;
          }
        }
      }

      // Prepare request payload
      const payload = {
        event_name: eventName,
        event_id: eventId,
        custom_data: customData,
        event_time: Math.floor(Date.now() / 1000), // Unix timestamp in seconds
        action_source: 'website'
      };

      // Add user data if available
      if (userData && Object.keys(userData).length > 0) {
        payload.user_data = userData;
      }

      // Send to backend (fire and forget - don't await)
      fetch(`${this.backendApiUrl}/api/facebook/conversion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      }).catch(() => {
        // Fail silently - don't break quiz flow
      });
    } catch (e) {
      // Fail silently - don't break quiz flow
    }
  }
}

// Expose globally so wizard-1-flow.js can use it
window.Wizard1Analytics = Wizard1Analytics;


